// src/components/ui/PhoneNumberInput/PhoneNumberInput.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Phone, ChevronDown, X, Search } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import 'flag-icons';
import styles from './PhoneNumberInput.module.css';

// Country data with dial codes
const COUNTRIES = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸', digitCount: 10, format: '(XXX) XXX-XXXX' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧', digitCount: 10, format: 'XXXX XXX XXX' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', digitCount: 10, format: '(XXX) XXX-XXXX' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺', digitCount: 9, format: 'XXX XXX XXX' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳', digitCount: 10, format: 'XXXXX XXXXX' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰', digitCount: 10, format: 'XXX XXX XXXX' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩', digitCount: 10, format: 'XXXX XXXXXX' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰', digitCount: 9, format: 'XX XXX XXXX' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪', digitCount: 9, format: 'XX XXX XXXX' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦', digitCount: 9, format: 'XX XXX XXXX' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳', digitCount: 11, format: 'XXX XXXX XXXX' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵', digitCount: 10, format: 'XX XXXX XXXX' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬', digitCount: 8, format: 'XXXX XXXX' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾', digitCount: 9, format: 'XX XXX XXXX' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭', digitCount: 9, format: 'XX XXX XXXX' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭', digitCount: 10, format: 'XXX XXX XXXX' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩', digitCount: 10, format: 'XXX XXX XXXX' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', flag: '🇻🇳', digitCount: 9, format: 'XX XXX XXXX' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪', digitCount: 10, format: 'XXX XXXXXXX' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', digitCount: 9, format: 'X XX XX XX XX' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹', digitCount: 10, format: 'XXX XXX XXXX' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸', digitCount: 9, format: 'XXX XXX XXX' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷', digitCount: 11, format: 'XX XXXXX XXXX' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽', digitCount: 10, format: 'XXX XXX XXXX' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦', digitCount: 9, format: 'XX XXX XXXX' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬', digitCount: 10, format: 'XXX XXX XXXX' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬', digitCount: 10, format: 'XXX XXX XXXX' },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪', digitCount: 9, format: 'XXX XXX XXX' },
];

const PhoneNumberInput = ({
  value = '',
  onChange,
  onValidationChange,
  error,
  placeholder = 'Enter phone number',
  disabled = false,
  required = false,
  className = '',
  id,
  name
}) => {
  const { user } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isTouched, setIsTouched] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize default country based on user's business country
  useEffect(() => {
    if (!selectedCountry) {
      const businessCountryCode = user?.business?.countryCode || 'LK';
      const defaultCountry = COUNTRIES.find(c => c.code === businessCountryCode) || COUNTRIES[0];
      setSelectedCountry(defaultCountry);
    }
  }, [user, selectedCountry]);

  // Parse initial value if provided
  useEffect(() => {
    if (value) {
      // Check if value starts with a dial code
      const matchedCountry = COUNTRIES.find(c => value.startsWith(c.dialCode));
      
      if (matchedCountry) {
        setSelectedCountry(matchedCountry);
        let number = value.substring(matchedCountry.dialCode.length).replace(/\D/g, '');
        // Remove leading zero if present (common in some countries after dial code)
        if (number.startsWith('0')) {
          number = number.substring(1);
        }
        setPhoneNumber(number);
      } else if (selectedCountry) {
        // No dial code found, try to extract digits and remove leading zero
        let number = value.replace(/\D/g, '');
        if (number.startsWith('0')) {
          number = number.substring(1);
        }
        setPhoneNumber(number);
      }
    } else if (value === '') {
      // Clear the phone number when value is empty
      setPhoneNumber('');
      setValidationError('');
      setIsTouched(false);
    }
  }, [value, selectedCountry]);

  // Filter countries based on search
  const filteredCountries = searchTerm.trim()
    ? COUNTRIES.filter(country =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.dialCode.includes(searchTerm) ||
        country.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : COUNTRIES;

  // Format phone number for display
  const formatPhoneNumber = (number, country) => {
    if (!number) return '';
    
    const digits = number.replace(/\D/g, '');
    const format = country.format;
    let formatted = '';
    let digitIndex = 0;

    for (let i = 0; i < format.length && digitIndex < digits.length; i++) {
      if (format[i] === 'X') {
        formatted += digits[digitIndex];
        digitIndex++;
      } else {
        formatted += format[i];
      }
    }

    // Add remaining digits if any
    if (digitIndex < digits.length) {
      formatted += digits.substring(digitIndex);
    }

    return formatted;
  };

  // Validate phone number
  const validatePhoneNumber = (number, country) => {
    if (!number) {
      if (required) {
        return 'Phone number is required';
      }
      return '';
    }

    const digits = number.replace(/\D/g, '');
    
    if (digits.length < country.digitCount) {
      return `Invalid phone number for ${country.name} — expected ${country.digitCount} digits.`;
    }

    if (digits.length > country.digitCount) {
      return `Phone number too long for ${country.name}.`;
    }

    return '';
  };

  // Notify parent of validation change
  const notifyValidationChange = (error) => {
    if (onValidationChange) {
      onValidationChange(!!error, error);
    }
  };

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowDropdown(false);
    setSearchTerm('');
    
    // Emit new value with updated country code
    const fullNumber = `${country.dialCode}${phoneNumber}`;
    onChange?.(fullNumber);
    
    // Revalidate with new country
    if (isTouched) {
      const error = validatePhoneNumber(phoneNumber, country);
      setValidationError(error);
      notifyValidationChange(error);
    }
  };

  // Strips dial code (only when input starts with '+'), non-digits, and one leading zero.
  // Shared by typing, paste and browser autofill so the leading 0 never reaches the parent.
  const normalizeRawNumber = (raw) => {
    const text = (raw || '').trim();
    let country = null;
    let numberPart = text;

    if (text.startsWith('+')) {
      const compact = text.replace(/[^\d+]/g, '');
      country = COUNTRIES.find(c => compact.startsWith(c.dialCode)) || null;
      if (country) numberPart = compact.substring(country.dialCode.length);
    }

    let digits = numberPart.replace(/\D/g, '');
    const lk = COUNTRIES.find(c => c.code === 'LK');
    if (!country && lk && selectedCountry?.code === lk.code && digits.length === lk.digitCount + 2 && digits.startsWith(lk.dialCode.slice(1))) {
      country = lk;
      digits = digits.substring(lk.dialCode.length - 1);
    }
    if (digits.startsWith('0')) {
      digits = digits.substring(1);
    }
    return { country, digits };
  };

  // Handle phone number input
  const handlePhoneInput = (e) => {
    const { country, digits } = normalizeRawNumber(e.target.value);
    const activeCountry = country || selectedCountry;

    if (country) {
      setSelectedCountry(country);
    }
    setPhoneNumber(digits);

    // Emit full number with country code
    const fullNumber = activeCountry ? `${activeCountry.dialCode}${digits}` : digits;
    onChange?.(fullNumber);

    // Validate on input if already touched
    if (isTouched) {
      const error = validatePhoneNumber(digits, activeCountry);
      setValidationError(error);
      notifyValidationChange(error);
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const { country: detectedCountry, digits } = normalizeRawNumber(e.clipboardData.getData('text'));

    if (detectedCountry) {
      setSelectedCountry(detectedCountry);
    }
    
    setPhoneNumber(digits);
    
    const fullNumber = (detectedCountry || selectedCountry).dialCode + digits;
    onChange?.(fullNumber);
    
    if (isTouched) {
      const error = validatePhoneNumber(digits, detectedCountry || selectedCountry);
      setValidationError(error);
      notifyValidationChange(error);
    }
  };

  // Handle blur - validate
  const handleBlur = () => {
    setIsTouched(true);
    if (selectedCountry) {
      const error = validatePhoneNumber(phoneNumber, selectedCountry);
      setValidationError(error);
      notifyValidationChange(error);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSearchTerm('');
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const displayError = error || validationError;
  const formattedNumber = selectedCountry ? formatPhoneNumber(phoneNumber, selectedCountry) : phoneNumber;

  return (
    <div className={`${styles.phoneNumberInput} ${className}`}>
      <div className={`${styles.inputContainer} ${displayError ? styles.error : ''} ${disabled ? styles.disabled : ''}`}>
        {/* Country Selector */}
        <div className={styles.countrySelector} ref={dropdownRef}>
          <button
            type="button"
            className={styles.countrySelectorButton}
            onClick={() => !disabled && setShowDropdown(!showDropdown)}
            disabled={disabled}
            aria-label="Country code"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
          >
            <span className={`fi fi-${selectedCountry?.code.toLowerCase()} ${styles.countryFlag}`}></span>
            <span className={styles.countryDialCode}>{selectedCountry?.dialCode}</span>
            <ChevronDown size={16} className={styles.chevronIcon} />
          </button>

          {showDropdown && (
            <div className={styles.countryDropdown} role="listbox">
              <div className={styles.countrySearch}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search countries..."
                  className={styles.countrySearchInput}
                  autoFocus
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className={styles.clearSearchButton}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <div className={styles.countryList}>
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      className={`${styles.countryOption} ${
                        selectedCountry?.code === country.code ? styles.selected : ''
                      }`}
                      onClick={() => handleCountrySelect(country)}
                      role="option"
                      aria-selected={selectedCountry?.code === country.code}
                    >
                      <span className={`fi fi-${country.code.toLowerCase()} ${styles.countryFlag}`}></span>
                      <span className={styles.countryName}>{country.name}</span>
                      <span className={styles.countryDialCode}>{country.dialCode}</span>
                    </button>
                  ))
                ) : (
                  <div className={styles.noResults}>No countries found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <div className={styles.phoneInputWrapper}>
          <Phone className={styles.phoneIcon} size={18} />
          <input
            ref={inputRef}
            type="tel"
            id={id}
            name={name}
            value={formattedNumber}
            onChange={handlePhoneInput}
            onPaste={handlePaste}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={styles.phoneInput}
            inputMode="tel"
            autoComplete="tel-national"
            aria-invalid={!!displayError}
            aria-describedby={displayError ? `${id}-error` : undefined}
          />
        </div>
      </div>

      {/* Error Message */}
      {displayError && (
        <span id={`${id}-error`} className={styles.errorText} role="alert">
          {displayError}
        </span>
      )}
    </div>
  );
};

export default PhoneNumberInput;